import { Box, Grid, MenuItem } from "@mui/material";
import { useTranslation } from "react-i18next";
import TextInput from "./TextInput";
import { useSchoolFee } from "../../context/schoolFeeContext";
import {
  PHONE_COUNTRIES,
  isCompleteNationalNumber,
  phoneCountry,
} from "../../constants/phoneCountries";

/**
 * The mobile-money account the payment debits.
 *
 * ⛔ NOT built on `PhoneNumberInputs`. That component is hard-wired to
 * `usePayment()` and to customer-wallet logic an anonymous school-fee payer
 * never has -- it reads `customerUser.customer_wallet_collection` and writes
 * the selected wallet, neither of which exists on this journey.
 *
 * ⛔ NO `length === 9`. The country decides the national-number length, and
 * the country comes from a registry (constants/phoneCountries.ts) so a second
 * country is a data row rather than a change here.
 */
const PayerPhoneInput = () => {
  const { t } = useTranslation();
  const {
    instrumentAccountNumber,
    setInstrumentAccountNumber,
    instrumentCountry,
    setInstrumentCountry,
  } = useSchoolFee();

  const country = phoneCountry(instrumentCountry);
  const complete = isCompleteNationalNumber(
    instrumentAccountNumber,
    instrumentCountry
  );
  const invalid = instrumentAccountNumber !== "" && !complete;

  return (
    <Box mb={4}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <TextInput
            select
            fullWidth
            id="schoolfee-phone-country"
            label={t("schoolfee.contact.country_label")}
            value={instrumentCountry}
            onChange={event => setInstrumentCountry(event.target.value)}
            variant="outlined"
          >
            {PHONE_COUNTRIES.map(entry => (
              <MenuItem key={entry.code} value={entry.code}>
                {`${entry.code} ${entry.dialCode}`}
              </MenuItem>
            ))}
          </TextInput>
        </Grid>

        <Grid item xs={12} sm={8}>
          <TextInput
            fullWidth
            type="tel"
            inputMode="numeric"
            id="schoolfee-phone"
            label={t("schoolfee.contact.phone_label")}
            value={instrumentAccountNumber}
            onChange={event =>
              setInstrumentAccountNumber(
                event.target.value.replace(/[^0-9]/g, "")
              )
            }
            error={invalid}
            helperText={
              invalid
                ? // `length`, not `count` -- `count` is i18next's plural
                  // selector and would silently look for _one/_other keys.
                  t("schoolfee.contact.phone_length", {
                    length: country?.nationalLength ?? 0,
                  })
                : t("schoolfee.contact.phone_hint")
            }
            placeholder={country?.example}
            variant="outlined"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default PayerPhoneInput;
